import supabase from './supabaseClient';

// ─────────────────────────────────────────────────────────────
// SIGN UP
// Creates a new user in Supabase Auth.
// Call this from RegisterScreen and SchoolRegisterScreen.
// After this, insert a matching row in the accounts table
// using the returned user.id as the primary key.
// ─────────────────────────────────────────────────────────────
export async function signUp(email, password, metadata = {}) {
  // metadata = { name, phone, role, schoolId (admins only) }
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: metadata, // stored in auth.users.raw_user_meta_data
      emailRedirectTo: 'https://edu-grade.com/auth/callback',
    },
  });
  if (error) throw error;
  return data; // { user, session }
}

// ─────────────────────────────────────────────────────────────
// SIGN IN
// Authenticates an existing user.
// Call this from LoginScreen.
// After this, fetch the user's profile from accounts table
// using the returned user.id.
// ─────────────────────────────────────────────────────────────
export async function signIn(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  if (error) throw error;
  return data; // { user, session }
}

// ─────────────────────────────────────────────────────────────
// SIGN OUT
// Ends the current session and clears the stored token.
// Call this from ProfileTab → Sign Out button.
// ─────────────────────────────────────────────────────────────
export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────
// GET CURRENT SESSION
// Returns the active session or null if not logged in.
// Call this in useEffect on app load to restore sessions
// after a page refresh.
// ─────────────────────────────────────────────────────────────
export async function getSession() {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw error;
  return session; // null if not logged in
}

// ─────────────────────────────────────────────────────────────
// GET CURRENT USER
// Returns the currently authenticated user object or null.
// ─────────────────────────────────────────────────────────────
export async function getCurrentUser() {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) return null;
  return user;
}

// ─────────────────────────────────────────────────────────────
// SEND PASSWORD RESET EMAIL
// Sends a password reset link to the given email address.
// Update the redirectTo URL to your actual deployed app URL.
// ─────────────────────────────────────────────────────────────
export async function resetPassword(email) {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: 'https://edu-grade-rho.vercel.app/reset-password',
  });
  if (error) throw error;
}
// ─────────────────────────────────────────────────────────────
// UPDATE PASSWORD
// Updates the password for the currently logged-in user.
// Call this from ChangePassModal in ProfileTab.
// The user must already be authenticated (session active).
// ─────────────────────────────────────────────────────────────
export async function updatePassword(newPassword) {
  const { error } = await supabase.auth.updateUser({
    password: newPassword,
  });
  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────
// LISTEN FOR AUTH STATE CHANGES
// Registers a callback that fires whenever the auth state
// changes — login, logout, token refresh, or user update.
// Call this once in your Root component's useEffect.
// Returns a subscription object — call unsubscribe() on cleanup.
//
// Usage:
//   const sub = onAuthStateChange((event, session) => {
//     if (event === 'SIGNED_OUT') logout();
//   });
//   return () => sub.unsubscribe(); // cleanup
// ─────────────────────────────────────────────────────────────
export function onAuthStateChange(callback) {
  const { data: { subscription } } = supabase.auth.onAuthStateChange(
    (event, session) => {
      // Possible events:
      // SIGNED_IN         — user logged in
      // SIGNED_OUT        — user logged out
      // TOKEN_REFRESHED   — JWT automatically refreshed
      // USER_UPDATED      — user metadata changed
      // PASSWORD_RECOVERY — user clicked password reset link
      callback(event, session);
    }
  );
  return subscription;
}
